import { useMemo, useState } from 'react';
import { api, ApiError, formatDate, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Page, SeafarerRecord } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { Detail, EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, TabStrip } from '../../ui/components';

const REGISTRAR_ROLES = ['SEAFARER_REGISTRAR'];

/** M22 — seafarer records: STCW certificates, endorsements, sea-service ledger, medical fitness. */
export function Seafarers() {
  const { persona } = useAuth();
  const isRegistrar = persona ? persona.roles.some((r) => REGISTRAR_ROLES.includes(r)) : false;
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [status, setStatus] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showRegister, setShowRegister] = useState(false);

  const path = useMemo(() => {
    const params = new URLSearchParams({ limit: '50' });
    if (status) params.set('status', status);
    if (appliedQ) params.set('q', appliedQ);
    return `/api/v1/m22/seafarers?${params.toString()}`;
  }, [status, appliedQ]);

  const listQ = useApiQuery<Page<SeafarerRecord>>(path);

  return (
    <div>
      <PageHeader
        title="Seafarers"
        subtitle="Seafarer's book, STCW certificates, endorsements, sea-service ledger, and medical fitness (M22 — SMSA)."
        actions={
          <>
            <form
              className="flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                setAppliedQ(q);
              }}
            >
              <input className="field-input !w-48" placeholder="Name, seafarer no. or NIN…" value={q} onChange={(e) => setQ(e.target.value)} />
              <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="">All statuses</option>
                {['ACTIVE', 'SUSPENDED', 'REVOKED'].map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <button type="submit" className="btn-secondary">
                Search
              </button>
            </form>
            {isRegistrar ? (
              <button className="btn-primary" onClick={() => setShowRegister(true)}>
                + Register seafarer
              </button>
            ) : null}
          </>
        }
      />

      {!isRegistrar ? <RoleNotice roles={REGISTRAR_ROLES} /> : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}

      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No seafarer records match" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Seafarer no.</th>
                <th className="table-head">Name</th>
                <th className="table-head">Nationality</th>
                <th className="table-head">Identity</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((s) => (
                <tr key={s.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelectedId(s.id)}>
                  <td className="table-cell font-mono text-xs font-semibold">{s.seafarerNo}</td>
                  <td className="table-cell">{s.fullName}</td>
                  <td className="table-cell text-xs text-slate-500">{s.nationality}</td>
                  <td className="table-cell font-mono text-xs text-slate-500">{s.isForeign ? `Foreign · ${s.foreignIdNo}` : s.nin}</td>
                  <td className="table-cell">
                    <StatusBadge status={s.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showRegister ? (
        <RegisterSeafarerModal
          onClose={() => setShowRegister(false)}
          onCreated={() => {
            setShowRegister(false);
            listQ.reload();
          }}
        />
      ) : null}

      {selectedId ? (
        <SeafarerDetailModal
          id={selectedId}
          isRegistrar={isRegistrar}
          onClose={() => setSelectedId(null)}
          onChanged={() => listQ.reload()}
        />
      ) : null}
    </div>
  );
}

function RegisterSeafarerModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [isForeign, setIsForeign] = useState(false);
  const [nin, setNin] = useState('');
  const [foreignIdNo, setForeignIdNo] = useState('');
  const [fullName, setFullName] = useState('');
  const [nationality, setNationality] = useState('Seychellois');
  const [dob, setDob] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/m22/seafarers', {
        isForeign,
        ...(isForeign ? { foreignIdNo: foreignIdNo.trim() } : { nin: nin.trim() }),
        fullName: fullName.trim(),
        nationality: nationality.trim(),
        dob: new Date(dob).toISOString(),
      });
      onCreated();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to register seafarer');
    } finally {
      setBusy(false);
    }
  };

  const valid = fullName.trim().length >= 2 && nationality.trim() && dob && (isForeign ? foreignIdNo.trim() : nin.trim());

  return (
    <Modal title="Register a seafarer" onClose={onClose} wide>
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-3">
        <label className="col-span-2 flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={isForeign} onChange={(e) => setIsForeign(e.target.checked)} />
          Foreign seafarer (no Seychellois NIN)
        </label>
        {isForeign ? (
          <Field label="Foreign id no.">
            <input className="field-input" value={foreignIdNo} onChange={(e) => setForeignIdNo(e.target.value)} />
          </Field>
        ) : (
          <Field label="NIN">
            <input className="field-input" value={nin} onChange={(e) => setNin(e.target.value)} />
          </Field>
        )}
        <Field label="Full name">
          <input className="field-input" value={fullName} onChange={(e) => setFullName(e.target.value)} />
        </Field>
        <Field label="Nationality">
          <input className="field-input" value={nationality} onChange={(e) => setNationality(e.target.value)} />
        </Field>
        <Field label="Date of birth">
          <input className="field-input" type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
        </Field>
      </div>
      <button className="btn-primary mt-4" disabled={busy || !valid} onClick={submit}>
        {busy ? 'Registering…' : "Issue seafarer's book"}
      </button>
    </Modal>
  );
}

type Tab = 'certificates' | 'endorsements' | 'sea-service';

function SeafarerDetailModal({ id, isRegistrar, onClose, onChanged }: { id: string; isRegistrar: boolean; onClose: () => void; onChanged: () => void }) {
  const q = useApiQuery<SeafarerRecord>(`/api/v1/m22/seafarers/${id}`);
  const [tab, setTab] = useState<Tab>('certificates');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showMedical, setShowMedical] = useState(false);
  const [medicalCertRef, setMedicalCertRef] = useState('');
  const [medicalCertSha256, setMedicalCertSha256] = useState('0'.repeat(64));
  const [medicalExpiresAt, setMedicalExpiresAt] = useState('');
  const [fit, setFit] = useState(true);
  const [showSuspend, setShowSuspend] = useState(false);
  const [reason, setReason] = useState('');

  // Certificate form
  const [certCode, setCertCode] = useState('');
  const [certNo, setCertNo] = useState('');
  const [issuingAuthority, setIssuingAuthority] = useState('SMSA');
  const [issuedAt, setIssuedAt] = useState('');
  const [certExpiresAt, setCertExpiresAt] = useState('');

  // Endorsement form
  const [endorsementCode, setEndorsementCode] = useState('');
  const [label, setLabel] = useState('');
  const [endorsementExpiresAt, setEndorsementExpiresAt] = useState('');

  // Sea service form
  const [vesselNameExternal, setVesselNameExternal] = useState('');
  const [rank, setRank] = useState('');
  const [signOnAt, setSignOnAt] = useState('');
  const [signOffAt, setSignOffAt] = useState('');

  const call = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      q.reload();
      onChanged();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Request failed');
    } finally {
      setBusy(false);
    }
  };

  const s = q.data;

  return (
    <Modal title={s ? `${s.fullName} — ${s.seafarerNo}` : 'Seafarer record'} onClose={onClose} wide>
      {q.error ? <ErrorNote message={q.error} /> : null}
      {q.loading ? <Spinner /> : null}
      {error ? (
        <div className="mb-3">
          <ErrorNote message={error} />
        </div>
      ) : null}
      {s ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3">
            <Detail label="Status" value={<StatusBadge status={s.status} />} />
            <Detail label="Nationality" value={s.nationality} />
            <Detail label="Date of birth" value={formatDate(s.dob)} />
            <Detail label="Medical fitness" value={s.medicalFit === undefined || s.medicalFit === null ? '—' : s.medicalFit ? 'Fit' : 'Unfit'} />
            <Detail label="Medical expires" value={s.medicalExpiresAt ? formatDate(s.medicalExpiresAt) : '—'} />
            {s.docVaultBookRef ? <Detail label="Book ref" value={s.docVaultBookRef} mono /> : null}
          </div>

          {isRegistrar ? (
            <div className="flex flex-wrap gap-2">
              {s.status === 'ACTIVE' ? (
                !showSuspend ? (
                  <button className="btn-danger" onClick={() => setShowSuspend(true)}>
                    Suspend
                  </button>
                ) : (
                  <div className="flex items-end gap-2">
                    <Field label="Reason">
                      <input className="field-input" value={reason} onChange={(e) => setReason(e.target.value)} />
                    </Field>
                    <button
                      className="btn-danger"
                      disabled={busy || reason.trim().length < 3}
                      onClick={() => call(() => api.post(`/api/v1/m22/seafarers/${s.id}/suspend`, { reason: reason.trim() }))}
                    >
                      Confirm
                    </button>
                  </div>
                )
              ) : null}
              {s.status === 'SUSPENDED' ? (
                <button className="btn-success" disabled={busy} onClick={() => call(() => api.post(`/api/v1/m22/seafarers/${s.id}/reinstate`))}>
                  Reinstate
                </button>
              ) : null}
              {!showMedical ? (
                <button className="btn-secondary" onClick={() => setShowMedical(true)}>
                  Record medical
                </button>
              ) : null}
            </div>
          ) : (
            <RoleNotice roles={REGISTRAR_ROLES} />
          )}

          {isRegistrar && showMedical ? (
            <div className="grid grid-cols-2 gap-3 rounded-lg border border-slate-200 p-3 sm:grid-cols-4">
              <Field label="Cert ref">
                <input className="field-input" value={medicalCertRef} onChange={(e) => setMedicalCertRef(e.target.value)} />
              </Field>
              <Field label="Sha256 (test fixture)">
                <input className="field-input font-mono text-xs" value={medicalCertSha256} onChange={(e) => setMedicalCertSha256(e.target.value)} />
              </Field>
              <Field label="Expires">
                <input className="field-input" type="date" value={medicalExpiresAt} onChange={(e) => setMedicalExpiresAt(e.target.value)} />
              </Field>
              <Field label="Fit for duty">
                <select className="field-input" value={fit ? 'yes' : 'no'} onChange={(e) => setFit(e.target.value === 'yes')}>
                  <option value="yes">Fit</option>
                  <option value="no">Unfit</option>
                </select>
              </Field>
              <button
                className="btn-primary col-span-2 sm:col-span-4"
                disabled={busy || !medicalCertRef.trim() || !medicalExpiresAt}
                onClick={() =>
                  call(() =>
                    api.post(`/api/v1/m22/seafarers/${s.id}/medical`, {
                      medicalCertRef: medicalCertRef.trim(),
                      medicalCertSha256,
                      expiresAt: new Date(medicalExpiresAt).toISOString(),
                      fit,
                    }),
                  )
                }
              >
                {busy ? 'Recording…' : 'Save medical record'}
              </button>
            </div>
          ) : null}

          <TabStrip
            tabs={[
              { key: 'certificates', label: `Certificates (${s.certificates?.length ?? 0})` },
              { key: 'endorsements', label: `Endorsements (${s.endorsements?.length ?? 0})` },
              { key: 'sea-service', label: `Sea service (${s.seaService?.length ?? 0})` },
            ]}
            active={tab}
            onChange={setTab}
          />

          {tab === 'certificates' ? (
            <div className="space-y-3">
              {(s.certificates ?? []).map((c) => (
                <div key={c.id} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2 text-sm">
                  <div>
                    <div className="font-medium">{c.certCode} · {c.certNo}</div>
                    <div className="text-xs text-slate-500">
                      {c.issuingAuthority} · issued {formatDate(c.issuedAt)}
                      {c.expiresAt ? ` · expires ${formatDate(c.expiresAt)}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={c.status} />
                    {isRegistrar && c.status === 'ACTIVE' ? (
                      <button
                        className="btn-danger !py-1 !text-xs"
                        disabled={busy}
                        onClick={() => {
                          const r = window.prompt('Revocation reason?');
                          if (r) call(() => api.post(`/api/v1/m22/seafarers/${s.id}/certificates/${c.id}/revoke`, { reason: r }));
                        }}
                      >
                        Revoke
                      </button>
                    ) : null}
                  </div>
                </div>
              ))}
              {isRegistrar ? (
                <div className="grid grid-cols-2 gap-3 rounded-lg border border-dashed border-slate-300 p-3 sm:grid-cols-3">
                  <Field label="Cert code">
                    <input className="field-input" value={certCode} onChange={(e) => setCertCode(e.target.value)} placeholder="STCW_BASIC_SAFETY" />
                  </Field>
                  <Field label="Cert no.">
                    <input className="field-input" value={certNo} onChange={(e) => setCertNo(e.target.value)} />
                  </Field>
                  <Field label="Issuing authority">
                    <input className="field-input" value={issuingAuthority} onChange={(e) => setIssuingAuthority(e.target.value)} />
                  </Field>
                  <Field label="Issued at">
                    <input className="field-input" type="date" value={issuedAt} onChange={(e) => setIssuedAt(e.target.value)} />
                  </Field>
                  <Field label="Expires (optional)">
                    <input className="field-input" type="date" value={certExpiresAt} onChange={(e) => setCertExpiresAt(e.target.value)} />
                  </Field>
                  <div className="flex items-end">
                    <button
                      className="btn-primary"
                      disabled={busy || !certCode.trim() || !certNo.trim() || !issuedAt}
                      onClick={() =>
                        call(() =>
                          api.post(`/api/v1/m22/seafarers/${s.id}/certificates`, {
                            certCode: certCode.trim(),
                            certNo: certNo.trim(),
                            issuingAuthority: issuingAuthority.trim(),
                            issuedAt: new Date(issuedAt).toISOString(),
                            ...(certExpiresAt ? { expiresAt: new Date(certExpiresAt).toISOString() } : {}),
                          }),
                        )
                      }
                    >
                      Issue certificate
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {tab === 'endorsements' ? (
            <div className="space-y-3">
              {(s.endorsements ?? []).map((e) => (
                <div key={e.id} className="rounded-lg border border-slate-100 px-3 py-2 text-sm">
                  <div className="font-medium">{e.label}</div>
                  <div className="text-xs text-slate-500">
                    {e.endorsementCode}
                    {e.expiresAt ? ` · expires ${formatDate(e.expiresAt)}` : ''}
                  </div>
                </div>
              ))}
              {isRegistrar ? (
                <div className="grid grid-cols-2 gap-3 rounded-lg border border-dashed border-slate-300 p-3 sm:grid-cols-3">
                  <Field label="Endorsement code">
                    <input className="field-input" value={endorsementCode} onChange={(e) => setEndorsementCode(e.target.value)} />
                  </Field>
                  <Field label="Label">
                    <input className="field-input" value={label} onChange={(e) => setLabel(e.target.value)} />
                  </Field>
                  <Field label="Expires (optional)">
                    <input className="field-input" type="date" value={endorsementExpiresAt} onChange={(e) => setEndorsementExpiresAt(e.target.value)} />
                  </Field>
                  <div className="col-span-2 flex items-end sm:col-span-3">
                    <button
                      className="btn-primary"
                      disabled={busy || !endorsementCode.trim() || !label.trim()}
                      onClick={() =>
                        call(() =>
                          api.post(`/api/v1/m22/seafarers/${s.id}/endorsements`, {
                            endorsementCode: endorsementCode.trim(),
                            label: label.trim(),
                            ...(endorsementExpiresAt ? { expiresAt: new Date(endorsementExpiresAt).toISOString() } : {}),
                          }),
                        )
                      }
                    >
                      Add endorsement
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          {tab === 'sea-service' ? (
            <div className="space-y-3">
              {(s.seaService ?? []).map((e) => (
                <div key={e.id} className="rounded-lg border border-slate-100 px-3 py-2 text-sm">
                  <div className="font-medium">
                    {e.rank} · {e.vesselNameExternal ?? e.vesselRegistrationId}
                  </div>
                  <div className="text-xs text-slate-500">
                    {formatDate(e.signOnAt)} – {e.signOffAt ? formatDate(e.signOffAt) : 'ongoing'}
                    {e.daysServed ? ` · ${e.daysServed} days` : ''}
                  </div>
                </div>
              ))}
              {isRegistrar ? (
                <div className="grid grid-cols-2 gap-3 rounded-lg border border-dashed border-slate-300 p-3 sm:grid-cols-4">
                  <Field label="Vessel name (external)">
                    <input className="field-input" value={vesselNameExternal} onChange={(e) => setVesselNameExternal(e.target.value)} />
                  </Field>
                  <Field label="Rank">
                    <input className="field-input" value={rank} onChange={(e) => setRank(e.target.value)} />
                  </Field>
                  <Field label="Sign-on">
                    <input className="field-input" type="date" value={signOnAt} onChange={(e) => setSignOnAt(e.target.value)} />
                  </Field>
                  <Field label="Sign-off (optional)">
                    <input className="field-input" type="date" value={signOffAt} onChange={(e) => setSignOffAt(e.target.value)} />
                  </Field>
                  <div className="col-span-2 flex items-end sm:col-span-4">
                    <button
                      className="btn-primary"
                      disabled={busy || !vesselNameExternal.trim() || !rank.trim() || !signOnAt}
                      onClick={() =>
                        call(() =>
                          api.post(`/api/v1/m22/seafarers/${s.id}/sea-service`, {
                            vesselNameExternal: vesselNameExternal.trim(),
                            rank: rank.trim(),
                            signOnAt: new Date(signOnAt).toISOString(),
                            ...(signOffAt ? { signOffAt: new Date(signOffAt).toISOString() } : {}),
                          }),
                        )
                      }
                    >
                      Record entry
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="border-t border-slate-100 pt-3 text-xs text-slate-400">Filed {formatDateTime(s.createdAt)}</div>
        </div>
      ) : null}
    </Modal>
  );
}
